import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import SimplePricing from "./simplePricing.component";
import CoinTopUp from "./coinTopUp.component";
import { useSubscription } from "@/lib/subscription/useSubscription.hook";
import { AnalyticsService } from "@/lib/analytics/analytics.service";

interface PricingModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Where the modal was opened from (analytics), e.g. "workspace" */
	source?: string;
}

/**
 * PricingModal component
 *
 * Z-index hierarchy:
 * - Toast notifications: z-100
 * - PricingModal content: z-80 (above CreatePodWorkspaceModal)
 * - PricingModal overlay: z-75
 * - Standard dialog content: z-70
 * - Standard dialog overlay: z-70
 */
export function PricingModal({
	open,
	onOpenChange,
	source = "workspace",
}: PricingModalProps) {
	const { t } = useTranslation();
	const { subscriptionStatus } = useSubscription();
	const isTrialing = subscriptionStatus === "TRIALING";
	// Active subscribers who ran out mid-cycle want coins, not a different plan,
	// so the copy leads with the top-up and the plan grid becomes the fallback.
	const isActiveSubscriber = subscriptionStatus === "ACTIVE";

	useEffect(() => {
		if (open) {
			AnalyticsService.track("pricing_modal_opened", { source });
		}
	}, [open, source]);

	const handleOpenChange = (next: boolean) => {
		if (!next) {
			// Dismissed without checkout (a checkout redirect leaves the page,
			// so this only fires for genuine dismissals).
			AnalyticsService.track("pricing_modal_closed", { source });
		}
		onOpenChange(next);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				className="sm:max-w-[95vw] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0 z-[80]"
				overlayClassName="z-[75]"
			>
				<DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
					<DialogTitle>
						{isActiveSubscriber
							? t("workspace.create.pricingTitleTopUp")
							: isTrialing
								? t("workspace.create.pricingTitleTrialing")
								: t("workspace.create.pricingTitle")}
					</DialogTitle>
					<DialogDescription>
						{isActiveSubscriber
							? t("workspace.create.pricingDescriptionTopUp")
							: isTrialing
								? t("workspace.create.pricingDescriptionTrialing")
								: t("workspace.create.pricingDescription")}
					</DialogDescription>
				</DialogHeader>
				<div className="overflow-y-auto flex-1 px-6 pb-6">
					{/* Self-hides unless the user has an active subscription. */}
					<div className="pt-2">
						<CoinTopUp
							source="workspace_modal"
							// Close on success so the user lands back on whatever the
							// missing coins were blocking. Bypasses handleOpenChange
							// because this is not a dismissal.
							onPurchaseComplete={() => onOpenChange(false)}
						/>
					</div>
					<div className="py-4 md:py-6">
						<SimplePricing />
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
